<script setup lang="ts">
import RepairSection from '@/components/rc/miner-repairs/repairs-sidebar/RepairSection.vue'
import SparePartSelect from '@/components/rc/spare-parts/SparePartSelect.vue'

import { useRepairsContext } from '@/composables/rc/drawer-context'
import { useMinerSparePartReplacements } from '@/composables/rc/miner-spare-part-replacements'
import { usePartDetails } from '@/composables/rc/part-details'
import { Icon } from '@iconify/vue'

const emit = defineEmits(['undo'])

defineProps<{ disabled?: boolean }>()

const { minerId, selectedPart } = useRepairsContext()
const { customerId, partReplacement, partId, partType } = usePartDetails(minerId, selectedPart)

const { useUndoPartReplacementMutation } = useMinerSparePartReplacements(minerId)
const { isPending, mutate } = useUndoPartReplacementMutation()

/**
 * Handle undo part replacement (send undo part replacement to server).
 */
const handleUndoPartReplacement = () => {
  if (!partId.value) return
  mutate(
    {
      part_id: partId.value,
      part_type: partType.value,
    },
    {
      onSuccess: () => {
        emit('undo')
      },
    }
  )
}
</script>

<template>
  <RepairSection title="Replacement" :step="1" :active="true">
    <div class="flex flex-col gap-2 w-full">
      <header class="inline-flex items-center justify-between">
        <el-text class="text-text-tertiary w-full">Replaced with:</el-text>

        <el-tooltip placement="bottom-end" content="Revert to original" effect="light" :disabled="disabled">
          <el-button link :disabled="disabled" @click="handleUndoPartReplacement">
            <Icon v-if="isPending" icon="eos-icons:three-dots-loading" height="20" />
            <Icon v-else icon="material-symbols:undo-rounded" height="20" />
          </el-button>
        </el-tooltip>
      </header>

      <div v-if="partReplacement" class="flex flex-col gap-2">
        <div class="flex gap-xs">
          <SparePartSelect
            v-if="customerId && partReplacement"
            v-model="partReplacement"
            :disabled="!!partReplacement || isPending || disabled"
            :part-type="partType"
            :customer-id="customerId"
          />
        </div>
        <p v-if="partReplacement?.owner" class="text-xs text-text-placeholder">Owned by {{ partReplacement.owner }}</p>
      </div>
    </div>
  </RepairSection>
</template>
